import { useMockData } from "../constants/environment";
import { Ministry } from "../models/ministry/ministry";
import { getMinistriesLocalized, MINISTRY_ID } from "../mock-data/ministry/ministry-mock-data";
import { CacheUtil } from "../utils/cache-util";
import { Language } from "../constants/language";
import {PaginatedResult, PAGINATION, PaginationUtil} from "../utils/pagination-util";

export interface MinistryFilter {
  category?: string[];
  featured?: boolean;
  duration?: string[];
  hasDuration?: boolean;
  [key: string]: any;
}

export interface MinistrySortOption {
  field: keyof Ministry | string;
  direction: 'asc' | 'desc';
}

class MinistryService {
    private static readonly MINISTRY_DATA_CACHE_KEY = "ministryContent";

    public async getMinistries(
      language: Language = Language.AR,
      filter?: MinistryFilter,
      sort?: MinistrySortOption,
      page: number = PAGINATION.DEFAULT_PAGE,
      limit: number = PAGINATION.DEFAULT_PAGE_SIZE,
      fetchAll: boolean = false
    ): Promise<PaginatedResult<Ministry>>{

        let ministries: Ministry[];
        let paginatedResult: PaginatedResult<Ministry>;

        const cachedData = CacheUtil.get<Ministry[]>(MinistryService.MINISTRY_DATA_CACHE_KEY);
        if (cachedData) {
          ministries = cachedData;
          paginatedResult = PaginationUtil.paginate<Ministry>(ministries, page, limit);
        }
        else {
            if (useMockData) {
                paginatedResult = getMinistriesLocalized(language, true);
                ministries = paginatedResult.data;
            }
            else {
                // Call APIs here to fetch real data
                paginatedResult = getMinistriesLocalized(language, true);
                ministries = paginatedResult.data;
          }
        }

        CacheUtil.set(MinistryService.MINISTRY_DATA_CACHE_KEY, ministries);

        if (filter) ministries = this.applyFilters(ministries, filter);
        if (sort)   ministries = this.applySorting(ministries, sort);

        if(filter || sort) paginatedResult = PaginationUtil.paginate<Ministry>(ministries, page, limit); //Update Pagination if filtering or sorting applied
        return paginatedResult;
    }


    public async getFeaturedMinistries(language: Language = Language.AR, limit: number = PAGINATION.DEFAULT_PAGE_SIZE): Promise<Ministry[]> {
      const ministryResult = getMinistriesLocalized(language);
      const ministries: Ministry[] = ministryResult.data;

      // Filter out inactive ministries first, then slice
      const activeMinistries = ministries.filter(ministry => ministry.isActive);
      return activeMinistries.slice(0, limit);
    }

    public async getMinistryById(id: MINISTRY_ID, language: Language = Language.AR): Promise<Ministry | null> {
      const ministryResult = getMinistriesLocalized(language);
      const ministries: Ministry[] = ministryResult.data;
      
      return ministries.find(ministry => ministry.id === id) || null;
    }


    private applyFilters(ministries: Ministry[], filter: MinistryFilter): Ministry[] {
      return ministries.filter(ministry => {
        if (filter.category && filter.category.length > 0) {
          if (!filter.category.includes(ministry.category)) return false;
        }

        if (filter.duration && filter.duration.length > 0) {
          if (!ministry.duration || !filter.duration.includes(ministry.duration)) return false;
        }

        if (filter.hasDuration !== undefined) {
          if (ministry.hasDuration !== filter.hasDuration) return false;
        }

        return true;
      });
    }

    private applySorting(ministries: Ministry[], sort: MinistrySortOption): Ministry[] {
      return [...ministries].sort((a, b) => {
        let aValue: any = a[sort.field as keyof Ministry];
        let bValue: any = b[sort.field as keyof Ministry];

        // Handle string fields
        if (typeof aValue === 'string' && typeof bValue === 'string') {
          aValue = aValue.toLowerCase();
          bValue = bValue.toLowerCase();
        }

        if (sort.direction === 'asc') {
          return aValue > bValue ? 1 : aValue < bValue ? -1 : 0;
        } else {
          return aValue < bValue ? 1 : aValue > bValue ? -1 : 0;
        }
      });
    }
}

export const ministryService = new MinistryService();
